import { Typography } from 'components';
import { motion } from 'framer-motion';
import { FileAddIcon } from 'lib/svgs/Icons';
import { useTranslations } from 'next-intl';
import {
  ChangeEvent,
  DragEvent,
  FC,
  HTMLAttributes,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { cn } from 'utils/cn';

import 'styles/sass/dropzone.scss';
import { ALLOWED_TYPES_CONTEXT_LIBRARY } from './ModalUploadContextLibrary';

type IFileAddContext = HTMLAttributes<HTMLInputElement>;

const FileAddContext: FC<IFileAddContext> = ({ onChange }) => {
  const t = useTranslations();

  const [dragOver, setDragOver] = useState(false);
  const dropboxRef = useRef<HTMLFormElement | null>(null);

  const acceptableFiles = useMemo(
    () =>
      ALLOWED_TYPES_CONTEXT_LIBRARY.reduce(
        (acc, value, index) =>
          acc + `.${value}${index !== ALLOWED_TYPES_CONTEXT_LIBRARY.length - 1 ? ', ' : ''}`,
        ''
      ),
    []
  );

  const preventAll = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const onDrop = useCallback(
    (e: DragEvent<HTMLInputElement>) => {
      preventAll(e);
      setDragOver(false);
      if (!e.dataTransfer || !e.dataTransfer.files) return;

      return onChange?.({
        target: { files: e.dataTransfer.files }
      } as ChangeEvent<HTMLInputElement>);
    },
    [onChange]
  );

  const handleDragOver = (e: DragEvent<HTMLFormElement>) => {
    preventAll(e);
    setDragOver(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLFormElement>) => {
    preventAll(e);
    setDragOver(false);
  };

  useEffect(() => {
    const currentRef = dropboxRef?.current;

    if (currentRef) {
      currentRef.addEventListener('dragenter', preventAll as unknown as EventListener, false);
      currentRef.addEventListener('drop', onDrop as unknown as EventListener, false);

      return () => {
        currentRef.removeEventListener('dragenter', preventAll as unknown as EventListener);
        currentRef.removeEventListener('drop', onDrop as unknown as EventListener);
      };
    }
  }, [dropboxRef, onDrop]);

  return (
    <motion.form
      encType="multipart/form-data"
      className={cn(
        'flex h-full flex-col items-center justify-center',
        'cursor-pointer',
        'duration-600 transition-all ease-in-out',
        'border-spacing-5 group-hover:border-opacity-100',
        dragOver ? 'dropzone' : 'border border-dashed border-tertiary-600',
        '!rounded-[8px] !p-0'
      )}
      ref={dropboxRef}
      whileTap={{ scale: 0.85 }}
      transition={{
        type: 'spring',
        stiffness: 400,
        damping: 10,
        restDelta: 7,
        mass: 0.5
      }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
    >
      <input
        className="hidden"
        type="file"
        id="file"
        name="file"
        multiple
        onChange={onChange}
        accept={acceptableFiles}
      />
      <label
        className="flex w-full flex-1 cursor-pointer flex-col items-center gap-1 px-20 py-8"
        htmlFor="file"
      >
        <FileAddIcon className="mb-2" />
        <div>
          <Typography variant="medium4" className="select-none text-primary-green-100">
            {t('context_library.file_add.click_to')}{' '}
          </Typography>
          <Typography variant="regular4" className="select-none text-tertiary-100">
            {t('context_library.file_add.or_drag')}
          </Typography>
        </div>
        <Typography
          variant="light5"
          className="!m-0 !max-w-[17rem] select-none text-center !leading-4 text-tertiary-200"
        >
          {t('context_library.file_add.extension')}
        </Typography>
      </label>
    </motion.form>
  );
};

export default FileAddContext;
